/*
 * Risk numbers for stock / crypto / brokerage accounts (as opposed to the
 * prop-firm futures tools): position sizing, concentration, and drawdown /
 * volatility / value-at-risk from an account's daily value history.
 */

/**
 * Shares to buy so a stop-out loses a set share of the account.
 * Optionally capped so one position is never more than `maxPositionPct` of it.
 */
export function positionSize({ accountValue, riskPct, entry, stop, target, maxPositionPct }) {
  const value = Number(accountValue) || 0;
  const e = Number(entry) || 0;
  const st = Number(stop) || 0;
  const perShare = Math.abs(e - st);
  if (!value || !e || !st || !perShare || !(riskPct > 0)) return null;
  const riskDollars = value * (riskPct / 100);
  let shares = riskDollars / perShare;
  let cappedBy = null;
  if (maxPositionPct > 0) {
    const maxShares = (value * (maxPositionPct / 100)) / e;
    if (shares > maxShares) { shares = maxShares; cappedBy = 'max-position'; }
  }
  // Whole shares by default; fractional shown for small sizes (crypto, fractional brokers).
  const wholeShares = Math.floor(shares);
  const t = Number(target) || 0;
  const side = st < e ? 'long' : 'short';
  const reward = t ? (side === 'long' ? t - e : e - t) : null;
  return {
    side,
    perShare,
    riskDollars,
    shares,
    wholeShares,
    positionValue: wholeShares * e,
    positionPct: (wholeShares * e) / value,
    actualRisk: wholeShares * perShare,
    rr: reward != null && reward > 0 ? reward / perShare : null,
    rewardDollars: reward != null && reward > 0 ? wholeShares * reward : null,
    stopPct: perShare / e,
    cappedBy,
  };
}

/** Holdings by symbol across accounts, with weights and how concentrated they are. */
export function concentration(positions, { cash = 0 } = {}) {
  const bySymbol = new Map();
  positions.forEach((p) => {
    const v = Number(p.marketValue) || 0;
    if (!v) return;
    const key = String(p.symbol || '?').toUpperCase();
    const row = bySymbol.get(key) || { symbol: key, value: 0, accounts: new Set() };
    row.value += v;
    if (p.account) row.accounts.add(p.account);
    bySymbol.set(key, row);
  });
  const invested = [...bySymbol.values()].reduce((s, r) => s + Math.abs(r.value), 0);
  const total = invested + Math.max(0, Number(cash) || 0);
  const rows = [...bySymbol.values()]
    .map((r) => ({ symbol: r.symbol, value: r.value, accounts: [...r.accounts], weight: total ? Math.abs(r.value) / total : 0 }))
    .sort((a, b) => b.weight - a.weight);
  const hhi = rows.reduce((s, r) => s + r.weight ** 2, 0);
  return {
    rows,
    invested,
    total,
    top1: rows[0]?.weight || 0,
    top5: rows.slice(0, 5).reduce((s, r) => s + r.weight, 0),
    hhi,
    // "Acts like N equal positions": 1 / HHI.
    effectiveN: hhi ? 1 / hhi : 0,
  };
}

const percentile = (sorted, p) => {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
};

/**
 * From daily values: daily returns (net of deposits / withdrawals), volatility,
 * historical 1-day value at risk, drawdowns. points: [{ date, value, netFlow }].
 */
export function equityRisk(points, { confidence = 0.95 } = {}) {
  const returns = [];
  for (let i = 1; i < points.length; i += 1) {
    const prev = Number(points[i - 1].value) || 0;
    const pnl = (Number(points[i].value) || 0) - prev - (Number(points[i].netFlow) || 0);
    if (prev > 0) returns.push({ date: points[i].date, ret: pnl / prev, pnl });
  }
  const current = points.length ? Number(points[points.length - 1].value) || 0 : 0;
  let peak = -Infinity;
  let peakDate = null;
  let maxDd = 0;
  let maxDdDate = null;
  const ddSeries = points.map((p) => {
    const v = Number(p.value) || 0;
    if (v > peak) { peak = v; peakDate = p.date; }
    const dd = peak > 0 ? (v - peak) / peak : 0;
    if (dd < maxDd) { maxDd = dd; maxDdDate = p.date; }
    return { date: p.date, dd };
  });
  const n = returns.length;
  const mean = n ? returns.reduce((s, r) => s + r.ret, 0) / n : 0;
  const variance = n > 1 ? returns.reduce((s, r) => s + (r.ret - mean) ** 2, 0) / (n - 1) : 0;
  const dailyVol = Math.sqrt(variance);
  const sorted = returns.map((r) => r.ret).sort((a, b) => a - b);
  const varRet = n >= 20 ? percentile(sorted, 1 - confidence) : null;
  const tail = varRet == null ? [] : sorted.filter((r) => r <= varRet);
  const cvarRet = tail.length ? tail.reduce((s, r) => s + r, 0) / tail.length : null;
  const worst = returns.reduce((w, r) => (!w || r.ret < w.ret ? r : w), null);
  const best = returns.reduce((b, r) => (!b || r.ret > b.ret ? r : b), null);
  return {
    days: n,
    current,
    peak: Number.isFinite(peak) ? peak : 0,
    peakDate,
    currentDd: peak > 0 ? (current - peak) / peak : 0,
    maxDd,
    maxDdDate,
    dailyVol,
    annualVol: dailyVol * Math.sqrt(252),
    varPct: varRet,
    varDollars: varRet == null ? null : varRet * current,
    cvarPct: cvarRet,
    cvarDollars: cvarRet == null ? null : cvarRet * current,
    worst,
    best,
    downDays: returns.filter((r) => r.ret < 0).length,
    returns,
    ddSeries,
  };
}

/** What a market-wide drop would cost: each holding falls by `shock` (e.g. -0.2) times its beta (1 if unknown). */
export function stressLoss(rows, shock, betas = {}) {
  return rows.reduce((s, r) => s + r.value * shock * (Number(betas[r.symbol]) || 1), 0);
}

/**
 * Positions merged by symbol across accounts: total quantity, a weighted
 * average entry, the latest price and value. Shorts keep a negative quantity.
 */
export function holdingsBySymbol(positions) {
  const map = new Map();
  positions.forEach((p) => {
    const sym = String(p.symbol || '').toUpperCase();
    if (!sym) return;
    const side = String(p.side || '').toLowerCase();
    let qty = Number(p.quantity ?? p.qty) || 0;
    if (side === 'short' && qty > 0) qty = -qty;
    const value = Number(p.marketValue) || 0;
    const price = Number(p.currentPrice) || (qty ? Math.abs(value / qty) : 0);
    const entry = Number(p.avgEntryPrice) || 0;
    const row = map.get(sym) || { symbol: sym, quantity: 0, value: 0, price: 0, costQty: 0, cost: 0, accounts: [] };
    row.quantity += qty;
    row.value += value;
    if (price) row.price = price;
    if (entry && qty) { row.cost += entry * Math.abs(qty); row.costQty += Math.abs(qty); }
    if (p.account && !row.accounts.includes(p.account)) row.accounts.push(p.account);
    map.set(sym, row);
  });
  return [...map.values()]
    .filter((r) => r.quantity || r.value)
    .map((r) => ({
      symbol: r.symbol, quantity: r.quantity, value: r.value, price: r.price,
      avgEntry: r.costQty ? r.cost / r.costQty : null, accounts: r.accounts,
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
}

/**
 * A stop for one holding. rule: { method: 'percent' | 'price' | 'risk', value }.
 *   percent: stop that far from the current price (a trailing stop's level today)
 *   price:   a fixed stop price
 *   risk:    the most you're willing to lose from here, in dollars
 */
export function planStop(holding, rule) {
  const qty = Number(holding.quantity) || 0;
  const price = Number(holding.price) || 0;
  const v = Number(rule?.value);
  if (!qty || !price || !Number.isFinite(v) || v <= 0) return null;
  const long = qty > 0;
  const abs = Math.abs(qty);
  let stop;
  if (rule.method === 'price') stop = v;
  else if (rule.method === 'risk') stop = long ? price - v / abs : price + v / abs;
  else stop = long ? price * (1 - v / 100) : price * (1 + v / 100);
  if (stop <= 0) stop = 0;
  // Already through the stop: it would fill at about the current price.
  const triggered = long ? stop >= price : stop <= price;
  const lossFromNow = triggered ? 0 : abs * Math.abs(price - stop);
  const entry = Number(holding.avgEntry) || null;
  const pnlAtStop = entry ? (long ? (stop - entry) : (entry - stop)) * abs : null;
  return {
    stop,
    distancePct: Math.abs(price - stop) / price,
    lossFromNow,
    pnlAtStop,
    locksInGain: pnlAtStop != null && pnlAtStop > 0,
    triggered,
  };
}

/**
 * Trades to move holdings toward target weights.
 * holdings: [{ symbol, value, price }], cash: dollars not invested.
 * targets: { SYMBOL: percent, ..., CASH: percent } (percent of the total).
 * opts.band: skip a holding if it's within this many percentage points of target.
 * opts.buyOnly: no sells; spend the spare cash (plus opts.addCash) on what's under target.
 * opts.addCash: money you're about to deposit.
 */
export function rebalance(holdings, cash, targets, { band = 0, buyOnly = false, addCash = 0, fractional = false } = {}) {
  const add = Math.max(0, Number(addCash) || 0);
  const cashNow = (Number(cash) || 0) + add;
  const bySym = new Map(holdings.map((h) => [h.symbol, h]));
  const symbols = [...new Set([...holdings.map((h) => h.symbol), ...Object.keys(targets).filter((s) => s !== 'CASH')])];
  const invested = holdings.reduce((s, h) => s + (Number(h.value) || 0), 0);
  const total = invested + cashNow;
  const targetSum = Object.values(targets).reduce((s, v) => s + (Number(v) || 0), 0);
  const rows = symbols.map((symbol) => {
    const h = bySym.get(symbol) || { symbol, value: 0, price: 0 };
    const targetPct = Number(targets[symbol]) || 0;
    const value = Number(h.value) || 0;
    const targetValue = total * (targetPct / 100);
    const weight = total ? value / total : 0;
    const drift = weight * 100 - targetPct; // percentage points
    return { symbol, price: Number(h.price) || 0, value, weight, targetPct, targetValue, drift, diff: targetValue - value };
  });

  let trades;
  if (buyOnly) {
    const cashTarget = total * ((Number(targets.CASH) || 0) / 100);
    let budget = Math.max(0, cashNow - cashTarget);
    const under = rows.filter((r) => r.diff > 0 && Math.abs(r.drift) >= band);
    const need = under.reduce((s, r) => s + r.diff, 0);
    const scale = need > budget && need > 0 ? budget / need : 1;
    trades = rows.map((r) => {
      const amount = under.includes(r) ? r.diff * scale : 0;
      return { ...r, amount };
    });
    budget -= trades.reduce((s, t) => s + t.amount, 0);
  } else {
    trades = rows.map((r) => ({ ...r, amount: Math.abs(r.drift) >= band ? r.diff : 0 }));
  }

  trades = trades.map((t) => {
    let shares = null;
    let amount = t.amount;
    if (t.price && amount) {
      const raw = amount / t.price;
      shares = fractional ? Math.round(raw * 10000) / 10000 : Math.trunc(raw);
      amount = shares * t.price;
    }
    return { ...t, amount, shares, action: amount > 0.005 ? 'buy' : amount < -0.005 ? 'sell' : 'hold' };
  });
  const bought = trades.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const sold = trades.filter((t) => t.amount < 0).reduce((s, t) => s - t.amount, 0);
  return {
    trades,
    total,
    targetSum,
    cashNow,
    cashAfter: cashNow - bought + sold,
    bought,
    sold,
  };
}
